import { CasesTableParams } from '..'
import { useCases } from '../context/client-context'
import { CasesActionDialog } from './cases-action-dialog'
import { DeleteDialog } from '../../../components/delete-dialog'
import { toast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { remove_case, toggleCaseArchive } from '@/api/case'
import { Case } from '../data/schema';
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

interface CasesDialogsProps {
  params: CasesTableParams
  setParams: React.Dispatch<React.SetStateAction<CasesTableParams>>
  onCaseUpdate?: (updatedCase: Case) => void; // Add this line
}

export function CasesDialogs(props : CasesDialogsProps) {
  const i18n = useTranslation()
  const { open, setOpen, currentRow, setCurrentRow, prefillData, setPrefillData } = useCases()
  const location = useLocation()
  const processedTimestampRef = useRef<number>(0)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = location.state as any;

    if (state?.openCreateDialog && state?.timestamp) {
      // Prevent processing the same navigation event twice
      if (state.timestamp === processedTimestampRef.current) {
        return;
      }

      // Check if the event is fresh (within 2 seconds)
      if (Date.now() - state.timestamp < 2000) {
        processedTimestampRef.current = state.timestamp;
        setPrefillData(state.prefillData);
        setOpen('add');
      }
    }
  }, [location.state, setOpen, setPrefillData, open]);

  return (
    <>
      <CasesActionDialog
        key='case-add'
        open={open === 'add'}
        onOpenChange={() => setOpen('add')}
        setParams={props.setParams}
        params={props.params}
        prefillData={prefillData}
      />

      {currentRow && (
        <>
          <CasesActionDialog
            key={`case-edit-${currentRow.id}`}
            open={open === 'edit'}
            onOpenChange={() => {
              setOpen('edit')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            currentRow={currentRow}
            setParams={props.setParams}
            params={props.params}
            onUpdateSuccess={props.onCaseUpdate} // Add this line
          />

          <DeleteDialog
            key={`case-delete-${currentRow.id}`}
            open={open === 'delete'}
            onOpenChange={() => {
              setOpen('delete')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            onDeleted={() => {
              toast({
                title: i18n.t('case_removed', { case: currentRow.name }),
              })
              props.setParams({ ...props.params, page: 0 })
            }}
            getDeleteName={() => currentRow.name}
            deleteRow={() => remove_case(currentRow.id)}
            deleteTitle={i18n.t('delete_case')}
          />
          
          <ConfirmDialog
            key={`case-archive-${currentRow.id}`}
            open={open === 'archive'}
            onOpenChange={() => {
              setOpen('archive')
              setTimeout(() => {
                setCurrentRow(null)
              }, 500)
            }}
            title={currentRow.archived_at ? i18n.t('unarchive_case') : i18n.t('archive_case')}
            desc={currentRow.archived_at 
              ? i18n.t('confirm_unarchive_case', { case: currentRow.name }) as string
              : i18n.t('confirm_archive_case', { case: currentRow.name }) as string}
            cancelBtnText={i18n.t('cancel')}
            confirmText={currentRow.archived_at ? i18n.t('unarchive') : i18n.t('archive')}
            destructive={false}
            handleConfirm={async () => {
              try {
                const updatedCase = await toggleCaseArchive(currentRow.id);
                
                toast({
                  title: currentRow.archived_at 
                    ? i18n.t('case_unarchived', { case: currentRow.name }) 
                    : i18n.t('case_archived', { case: currentRow.name }),
                });
                
                if (props.onCaseUpdate) {
                  props.onCaseUpdate(updatedCase);
                } else {
                  props.setParams({ ...props.params, page: 0 });
                }
                setOpen(null);
              } catch {
                toast({
                  title: i18n.t('error'),
                  description: i18n.t('archive_error'),
                  variant: "destructive",
                });
              }
            }}
          />
        </>
      )}
    </>
  )
}
